import { useRef, useState } from 'react'
import { FocusTrapDialog } from '../../components/FocusTrapDialog'
import { InlineAlert } from '../../components/InlineAlert'
import { ApiError } from '../../api/problem'
import { createContact, patchContact } from '../../api/contacts'
import { AccountLookupField } from '../accounts/AccountLookupField'
import type { ContactView } from '../../types/contact'

export interface ContactFormValues {
  name: string
  email: string
  title: string
  account: { id: string; name: string } | null
}

export interface ContactFormModalProps {
  mode: 'create' | 'edit'
  /** Required in edit mode: the contact being edited and its known version (If-Match). */
  editing?: { contact: ContactView; version: number }
  onClose: () => void
  onSaved: (contact: ContactView) => void
}

/**
 * Deliberately lenient client-side mirror of the backend's `@Email`
 * validation (Jakarta Bean Validation) — good enough to stop an obviously
 * malformed address before a round trip; the backend's 422 remains the
 * source of truth (AC8) and its message surfaces via `error` on failure.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function initialValues(editing?: ContactFormModalProps['editing']): ContactFormValues {
  if (!editing) {
    return { name: '', email: '', title: '', account: null }
  }
  const c = editing.contact
  return {
    name: c.name,
    email: c.email,
    title: c.title ?? '',
    account: { id: c.accountId, name: c.accountName ?? c.accountId },
  }
}

/**
 * The single New-contact / Edit-contact form — real `POST`/`PATCH`, no
 * navigation on success (Contact has no detail page; the caller replaces
 * the row in place, mirroring `AccountFormModal`'s shape minus the
 * routing). Unlike `Branch.legalEntityId`, `Contact.accountId` is **not**
 * re-pointable on PATCH (design §2.4 — absent from `PatchContactRequest`),
 * so the Account field is read-only once a contact exists.
 */
export function ContactFormModal({ mode, editing, onClose, onSaved }: ContactFormModalProps) {
  const [values, setValues] = useState<ContactFormValues>(() => initialValues(editing))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  function update<K extends keyof ContactFormValues>(key: K, value: ContactFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const emailTrimmed = values.email.trim()
  const emailValid = emailTrimmed !== '' && EMAIL_PATTERN.test(emailTrimmed)
  const showEmailError = emailTrimmed !== '' && !emailValid
  const isValid = values.name.trim() !== '' && emailValid && values.account !== null

  async function handleSubmit() {
    if (!isValid || submitting || !values.account) return
    setSubmitting(true)
    setError(null)
    try {
      if (mode === 'create') {
        const { contact } = await createContact({
          accountId: values.account.id,
          name: values.name.trim(),
          email: emailTrimmed,
          title: values.title.trim() || null,
        })
        onSaved(contact)
      } else if (editing) {
        const { contact } = await patchContact(
          editing.contact.id,
          {
            name: values.name.trim(),
            email: emailTrimmed,
            title: values.title.trim() || null,
          },
          editing.version,
        )
        onSaved(contact)
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail ?? err.message)
      } else {
        setError('Something went wrong saving this contact. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FocusTrapDialog
      titleId="contact-form-title"
      title={mode === 'create' ? 'New contact' : `Edit ${editing?.contact.name ?? 'contact'}`}
      onClose={onClose}
      initialFocusRef={nameRef}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="iw-btn iw-btn--primary"
            onClick={handleSubmit}
            disabled={!isValid || submitting}
          >
            {submitting ? 'Saving…' : mode === 'create' ? 'Create contact' : 'Save changes'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="contact-name">
            Name
          </label>
          <input
            id="contact-name"
            ref={nameRef}
            className="iw-input"
            value={values.name}
            required
            onChange={(e) => update('name', e.target.value)}
          />
        </div>

        {mode === 'edit' ? (
          <div className="iw-field">
            <span className="iw-field-label" id="contact-account-label">
              Account
            </span>
            <div>
              <span className="iw-pill iw-pill--neutral">{values.account?.name}</span>
            </div>
            <span style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-subtle)' }}>
              The account cannot be changed once a contact is created.
            </span>
          </div>
        ) : (
          <AccountLookupField
            id="contact-account"
            label="Account"
            value={values.account}
            onChange={(next) => update('account', next)}
          />
        )}

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="contact-email">
            Email
          </label>
          <input
            id="contact-email"
            type="email"
            className="iw-input"
            value={values.email}
            required
            aria-invalid={showEmailError}
            aria-describedby={showEmailError ? 'contact-email-error' : undefined}
            onChange={(e) => update('email', e.target.value)}
          />
          {showEmailError ? (
            <span id="contact-email-error" role="alert" style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-danger)' }}>
              Enter a valid email address.
            </span>
          ) : null}
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="contact-title">
            Title (optional)
          </label>
          <input
            id="contact-title"
            className="iw-input"
            value={values.title}
            onChange={(e) => update('title', e.target.value)}
          />
        </div>
      </form>
    </FocusTrapDialog>
  )
}
